import React, { useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
  Pause,
  Play,
  Plus,
  RefreshCw,
  X,
  Zap,
} from "lucide-react";
import Card from "../components/Card.jsx";
import Pill from "../components/Pill.jsx";
import InfoTip from "../components/InfoTip.jsx";
import GradientHeader from "../components/GradientHeader.jsx";
import { Stranka, DlazdiceFiltr, PruhFiltru, RozbalovaciVyber } from "../components/ui.jsx";
import { SkeletonTable, SkeletonCards } from "../components/Skeleton.jsx";
import ConfirmDialog, { isConfirmBypassed, getGlobalConfirmBypassUntil, clearGlobalConfirmBypass } from "../components/ConfirmDialog.jsx";
import { useApiData, apiFetch } from "../lib/api.js";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronDown } from "lucide-react";

// POZOR: NETLIFY_RUNNER_MAP, CRON_BRIEF_DESCRIPTION, CRON_FREQUENCY,
// CRON_SCHEDULE_LABEL a CRON_DISPLAY_NAME byly **odstraneny 14.5.2026** ramci
// odstrihu od Notionu (refactor "vsechno do DB"). Vsechna data ted ctene primo
// z PG `automatizace` (sloupce runner_id, popis_kratky, cron_schedule_label,
// frequency_label, frequency_interval_h). Edit pres admin UI (modal Pridat/Upravit).
// Backwards-compat fallback do triggerLabel pokud cronScheduleLabel chybi.




// Vrací true pokud cron je "stale" — neběžel déle než tolerovaný interval.
// Čte frequency_interval_h z PG záznamu (po refactoru 14.5.2026).
// Default: 24h pro denní crony pokud chybí frequency_interval_h.
function isCronStale(it) {
  // 3.9.2026: bez cron rozvrhu nema "nebezelo dle planu" smysl. Rucne spoustene
  // automatizace a Apps Script triggery zadny plan v Netlify nemaji, takze se
  // hlasily jako zaseknute napořád (Sync portal data, DB cleanup dry-run).
  if (!it.cronSchedule) return false;
  if (!it.datumPosledniRealizace) return true;
  const ageH = (Date.now() - new Date(it.datumPosledniRealizace).getTime()) / 3600000;
  if (!Number.isFinite(ageH)) return true;
  const intervalH = it.frequencyIntervalH;
  if (intervalH != null) {
    // Threshold = 1.5x interval (hodinové) nebo 1.25x (denní/týdenní/měsíční)
    const threshold = intervalH * (intervalH < 24 ? 1.5 : 1.25);
    return ageH > threshold;
  }
  // Default denní cron: 30h threshold
  return ageH > 30;
}

// ============================================================================
// Stránka Automatizace
// ----------------------------------------------------------------------------
// Krok 1 migrace: read-only přehled stavu 14 automatizací z Notion DB Přehled.
// Endpoint /api/automatizace-status vrací seznam s posledním během (OK/NOK),
// datem, prioritou, kategorií a systémy.
// V dalších krocích doplníme manuální spouštění a postupně přesuneme
// jednotlivé Apps Scripts do Netlify Scheduled Functions.
// ============================================================================

// Relativní čas v češtině (před X minutami / hodinami / dny). Pokud není datum,
// vrátí pomlčku. Vstup: ISO string nebo null.
function relativeTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "—";
  const diff = Date.now() - then;
  if (diff < 0) return "v budoucnu";
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "právě teď";
  if (mins < 60) return `před ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `před ${hrs} h`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "včera";
  if (days < 7) return `před ${days} dny`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `před ${weeks} týdny`;
  const months = Math.floor(days / 30);
  return `před ${months} měsíci`;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
}

function StatusPill({ status }) {
  if (status === "OK") return <Pill tone="green">OK</Pill>;
  if (status === "NOK") return <Pill tone="red">Chyba</Pill>;
  return <Pill tone="slate">—</Pill>;
}

function PrioritaPill({ priorita }) {
  const tone =
    priorita === "High"
      ? "red"
      : priorita === "Medium"
      ? "amber"
      : priorita === "Low"
      ? "blue"
      : "slate";
  return <Pill tone={tone}>{priorita || "—"}</Pill>;
}

// Mapping zobrazeni Status hodnot. "Vytvoreno" zobrazujeme jako "Aktivni"
// (terminologicke sjednoceni). Filter prijme obe hodnoty, takze kdyz user
// prejmenuje v Notion select option, kod nepotrebuje update.
const STAV_DISPLAY = {
  "Vytvořeno": "Aktivní",
  "Aktivní": "Aktivní",
  "V přípravě": "V přípravě",
  "Backlog": "Backlog",
  "Archived": "Archived",
};

function StavPill({ stav }) {
  const display = STAV_DISPLAY[stav] || stav;
  const tone =
    display === "Aktivní"
      ? "green"
      : display === "V přípravě"
      ? "amber"
      : display === "Backlog"
      ? "slate"
      : display === "Archived"
      ? "slate"
      : "slate";
  return <Pill tone={tone}>{display || "—"}</Pill>;
}

// Souhrnné KPI nahoře — kolik automatizací běží OK, kolik selhalo, kolik
// neběželo dle plánu. Klikací: nastaví filter na odpovídající kategorii
// (toggle on/off, druhý klik vyčistí). Active dlaždice má ring outline.
function SummaryRow({ items, filter, onFilterChange }) {
  const stats = useMemo(() => {
    const total = items.length;
    let ok = 0;
    let nok = 0;
    let stale = 0; // neběželo déle než tolerovaný interval (per cron frekvence)
    items.forEach((it) => {
      if (it.stavPosledniRealizace === "OK") ok++;
      if (it.stavPosledniRealizace === "NOK") nok++;
      if (it.status === "Archived") return;
      if (isCronStale(it)) stale++;
    });
    return { total, ok, nok, stale };
  }, [items]);

  // Toggle filter: pokud je daný filter už aktivní, klik ho zruší.
  const toggle = (patch) => {
    // Zjištění zda je tento filter teď aktivní (porovnám klíče)
    const isActive = Object.keys(patch).every((k) => filter[k] === patch[k]);
    if (isActive) {
      // Reset jen těch klíčů co tahle dlaždice nastavovala
      const cleared = { ...filter };
      Object.keys(patch).forEach((k) => { cleared[k] = ""; });
      onFilterChange(cleared);
    } else {
      onFilterChange({ ...filter, ...patch });
    }
  };

  // Active flagy pro každou dlaždici. Default filter.status = "_hideArchived" (skryt archivovane)
  // se taky pocita jako "default state" pro KPI "Celkem".
  const isDefaultStatus = !filter.status || filter.status === "_hideArchived";
  const activeCelkem = !filter.stav && !filter.priorita && !filter.system && isDefaultStatus && !filter.search;
  const activeOK = filter.stav === "OK";
  const activeNOK = filter.stav === "NOK";
  const activeStale = filter.stav === "stale";

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
      <DlazdiceFiltr
        popisek="Celkem"
        hodnota={stats.total}
        ikona={Activity}
        ton="neutralni"
        info={<InfoTip text="Klik vyčistí všechny filtry a zobrazí všech 16 automatizací." />}
        zapnuto={activeCelkem}
        onClick={() => onFilterChange({ stav: "", priorita: "", system: "", status: "_hideArchived", search: "" })}
      />
      <DlazdiceFiltr
        popisek="OK"
        hodnota={stats.ok}
        ikona={CheckCircle2}
        ton="ok"
        info={<InfoTip text="Klik filtruje na automatizace, jejichž poslední běh skončil úspěšně. Druhý klik vrátí zpět." />}
        zapnuto={activeOK}
        onClick={() => toggle({ stav: "OK" })}
      />
      <DlazdiceFiltr
        popisek="Chyba"
        hodnota={stats.nok}
        ikona={AlertTriangle}
        ton="problem"
        info={<InfoTip text="Klik filtruje na automatizace s posledním NOK během. Vyžaduje zásah, Slack alert a Audit log." />}
        zapnuto={activeNOK}
        onClick={() => toggle({ stav: "NOK" })}
      />
      <DlazdiceFiltr
        popisek="Neběželo dle plánu"
        hodnota={stats.stale}
        ikona={Clock}
        ton="pozor"
        info={<InfoTip text="Aktivní cron měl běžet (dle schedule), ale poslední záznam je mimo očekávaný interval. Hodinové: tolerance 90 min, denní: 30 h, týdenní: 9 dní, měsíční: 33 dní. Většinou to znamená že trigger se nespustil, zkontroluj Netlify Function logs nebo spusť ručně." />}
        zapnuto={activeStale}
        onClick={() => toggle({ stav: "stale" })}
      />
    </div>
  );
}


// Filtry pro tabulku — stav posledního běhu, priorita, kategorie/systémy, hledání.
function Filters({ value, onChange, items }) {
  const set = (k, v) => onChange({ ...value, [k]: v });

  // Dynamicky odvodíme dostupné systémy z dat (žádné hardcoded enum).
  const systemyOptions = useMemo(() => {
    const s = new Set();
    items.forEach((it) => (it.systemy || []).forEach((x) => s.add(x)));
    return Array.from(s).sort();
  }, [items]);

  // 14.9.2026: filtry z holých <select> na pruh ze vzorníku. Na telefonu se
  // čtyři selecty pod sebou roztahovaly každý jinak široko.
  const zapnuto = !!(value.stav || value.priorita || value.system || value.search) || value.status !== "_hideArchived";

  return (
    <PruhFiltru
      className="mb-3"
      zapnuto={zapnuto}
      onZrusit={() => onChange({ stav: "", priorita: "", system: "", status: "_hideArchived", search: "" })}
    >
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <RozbalovaciVyber
          stitek="Stav"
          value={value.stav}
          onChange={(k) => set("stav", k)}
          items={[
            { key: "", label: "Všechny" },
            { key: "OK", label: "OK" },
            { key: "NOK", label: "Chyba" },
            { key: "none", label: "Bez záznamu" },
          ]}
        />
        <RozbalovaciVyber
          stitek="Priorita"
          value={value.priorita}
          onChange={(k) => set("priorita", k)}
          items={[
            { key: "", label: "Všechny" },
            { key: "High", label: "High" },
            { key: "Medium", label: "Medium" },
            { key: "Low", label: "Low" },
          ]}
        />
        <RozbalovaciVyber
          stitek="Systém"
          value={value.system}
          onChange={(k) => set("system", k)}
          items={[{ key: "", label: "Všechny" }, ...systemyOptions.map((x) => ({ key: x, label: x }))]}
        />
        <RozbalovaciVyber
          stitek="Zápis"
          value={value.status}
          onChange={(k) => set("status", k)}
          items={[
            { key: "_hideArchived", label: "Bez archivu" },
            { key: "", label: "S archivem" },
            { key: "Aktivní", label: "Jen Aktivní" },
            { key: "V přípravě", label: "Jen V přípravě" },
            { key: "Backlog", label: "Jen Backlog" },
            { key: "Archived", label: "Jen Archived" },
          ]}
        />
      </div>
      <input
        type="text"
        placeholder="Hledat název"
        value={value.search}
        onChange={(e) => set("search", e.target.value)}
        className="field w-full"
      />
    </PruhFiltru>
  );
}

export default function Automatizace() {
  const { data, loading, error, refetch, refreshing } = useApiData("/api/automatizace-status");
  // Pravidla pro sloupec "Pravidla" (počet aktivních záznamů per cron sekce).
  // Refresh se pojí s automatizace-status refresh (při manuálním Obnovit).
  const { data: pravidlaData } = useApiData("/api/pravidla");
  const items = (data && data.automatizace) || [];
  const pravidlaSnap = pravidlaData?.data || pravidlaData || null;

  // running = { [name]: { startedAt: ms } } — vícenásobné kliknutí povoleno,
  // tlačítko se nedeaktivuje. Inline countdown vedle tlačítka.
  const [running, setRunning] = useState({});
  // lastRun = { [name]: { ok, message, at, durationSec } }
  const [lastRun, setLastRun] = useState({});
  // confirmTarget = { name, id } pro ConfirmDialog
  const [confirmTarget, setConfirmTarget] = useState(null);
  // Expanded card detail — name → detail data (kešovano per cron). Toggle dropdown.
  const [expandedName, setExpandedName] = useState(null);
  const [detailCache, setDetailCache] = useState({});
  // Edit modal: null = closed; {} = pridat novou; { ...automatizace } = upravit existujici
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [runners, setRunners] = useState([]);
  // Pri prvnim otevreni modalu nacti seznam runneru z /api/automatizace-runners
  React.useEffect(() => {
    if (editTarget && runners.length === 0) {
      apiFetch("/api/automatizace-runners").then((r) => r.json()).then((j) => {
        if (j.ok) setRunners(j.runners || []);
      }).catch(() => { /* OK */ });
    }
  }, [editTarget]);

  async function toggleExpand(name) {
    if (expandedName === name) {
      setExpandedName(null);
      return;
    }
    setExpandedName(name);
    if (detailCache[name]) return;
    try {
      const r = await apiFetch(`/api/automatizace-status?detail=${encodeURIComponent(name)}`);
      const j = await r.json();
      if (j.ok && j.automatizace) {
        setDetailCache((c) => ({ ...c, [name]: j.automatizace }));
      } else {
        setDetailCache((c) => ({ ...c, [name]: { error: j.error || "Detail nenalezen" } }));
      }
    } catch (e) {
      setDetailCache((c) => ({ ...c, [name]: { error: e.message } }));
    }
  }
  // Globální rate-limit throttle: pokud běží 2+ crony paralelně, ostatní
  // tlačítka se zamknou na 10s (žlutě). Ochrana proti rychlému naklikání
  // 5+ cronů co by přetížily Sheets API (60 zápisů/min limit).
  const [globalThrottleUntil, setGlobalThrottleUntil] = useState(0);
  const isThrottleActive = globalThrottleUntil > Date.now();

  // tick — kazdou sekundu pokud něco běží/dokončilo se nedávno/throttle aktivní.
  // Jinak každou minutu (countdown bez ptaní 60m).
  const [, setTick] = useState(0);
  const hasRunning = Object.keys(running).length > 0;
  // hasRecent = nějaký lastRun finished v posledních 25s (5s Done + 20s Cooldown)
  const hasRecent = Object.values(lastRun).some((lr) => lr && lr.at && (Date.now() - lr.at) < 25000);
  React.useEffect(() => {
    const fast = hasRunning || hasRecent || isThrottleActive;
    const t = setInterval(() => setTick((n) => n + 1), fast ? 1000 : 60000);
    return () => clearInterval(t);
  }, [hasRunning, hasRecent, isThrottleActive]);

  // Auto-refresh toggle (default zapnutý) + interval 30s s pause při inactive tabu.
  // User po editaci Pravidla nebo manuálním spuštění uvidí čerstvé "Poslední"
  // bez nutnosti F5. Stejný pattern jako AuditLog.jsx.
  const [autoRefresh, setAutoRefresh] = useState(true);
  React.useEffect(() => {
    if (!autoRefresh) return undefined;
    const tick = () => {
      if (document.visibilityState === "visible") refetch();
    };
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [autoRefresh, refetch]);

  // Default: skryt archivovane automatizace (status === "Archived").
  // User je vidi po prepnuti selectu na "Vsechny" nebo "Archived".
  const [filter, setFilter] = useState({
    stav: "",
    priorita: "",
    system: "",
    status: "_hideArchived",
    search: "",
  });

  // Klik na "Spustit teď" → bypass check (per-akce + global) → buď přímé spuštění,
  // nebo otevři confirm dialog. Globální bypass aktivuje user checkbox v dialogu
  // s scopem="global" — pak se neptá na ŽÁDNOU automatizaci 60 min.
  const askRunConfirm = (name, runnerId) => {
    const id = runnerId;
    if (!id) return;
    if (isConfirmBypassed(`run:${id}`)) {
      // Bypass aktivni (per-akce nebo global) — spustime hned bez dialogu
      executeRun(name, id);
      return;
    }
    setConfirmTarget({ name, id });
  };

  // Vlastni spousteci flow vyclenen, aby ho slo zavolat z bypass i z dialogu.
  // Vícenásobné kliknutí povoleno (max 3 paralelně doporučeno).
  // State machine tlačítka:
  //   default   → modré "Spustit teď"
  //   running   → modré spinner "Běží Xs"
  //   done      → zelené "✓ Hotovo" (5 s)
  //   cooldown  → šedé disabled "Počkej Xs" (20 s ochrana před přetížením)
  //   error     → červené "Chyba" (10 s) + dropdown s message
  const executeRun = async (name, id) => {
    const startedAt = Date.now();
    // Před přidáním zjistím, kolik už běží — pokud bude >= 2 (po inkrementu),
    // aktivuji 10s throttle pro ostatní tlačítka (rate limit ochrana Sheets API).
    const wouldBeCount = Object.keys(running).length + 1;
    setRunning((s) => ({ ...s, [name]: { startedAt } }));
    if (wouldBeCount >= 2) {
      setGlobalThrottleUntil(Date.now() + 10000);
    }
    try {
      const r = await apiFetch("/api/automatizace-run", {
        method: "POST",
        body: JSON.stringify({ id }),
      });
      const j = await r.json();
      const isBackground = j.result?.background === true || (j.background === true);
      if (!r.ok || !j.ok) {
        const msg = (j && j.error) || `HTTP ${r.status}`;
        setLastRun((s) => ({
          ...s,
          [name]: { ok: false, error: msg, at: Date.now() },
        }));
      } else {
        setLastRun((s) => ({
          ...s,
          [name]: { ok: true, background: isBackground, at: Date.now() },
        }));
        setTimeout(() => refetch(), 1500);
      }
    } catch (e) {
      setLastRun((s) => ({
        ...s,
        [name]: { ok: false, error: e.message, at: Date.now() },
      }));
    } finally {
      setRunning((s) => {
        const next = { ...s };
        delete next[name];
        return next;
      });
    }
  };

  // Potvrzení v confirm dialogu → reálné spuštění
  const runNow = async () => {
    if (!confirmTarget) return;
    const { name, id } = confirmTarget;
    setConfirmTarget(null);
    await executeRun(name, id);
  };

  // Manualni zruseni 60min globálního bypassu (z hlavičky stránky).
  const cancelGlobalBypass = () => {
    clearGlobalConfirmBypass("run");
    setTick((n) => n + 1);
  };

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (filter.stav === "OK" && it.stavPosledniRealizace !== "OK") return false;
      if (filter.stav === "NOK" && it.stavPosledniRealizace !== "NOK") return false;
      if (filter.stav === "none" && it.stavPosledniRealizace) return false;
      // Klikací KPI "Neběželo dle plánu": filter stav="stale"
      if (filter.stav === "stale") {
        if (it.status === "Archived") return false;
        if (!isCronStale(it)) return false;
      }
      if (filter.priorita && it.priorita !== filter.priorita) return false;
      if (filter.system && !(it.systemy || []).includes(filter.system)) return false;
      if (filter.status === "_hideArchived") {
        // Default: skryt archivovane
        if (it.status === "Archived") return false;
      } else if (filter.status) {
        // Akceptujeme "Aktivni" jako alias pro "Vytvoreno" (i naopak), aby
        // filter fungoval bez ohledu na to, zda je v Notion select prejmenovan.
        const displayStav = STAV_DISPLAY[it.status] || it.status;
        if (displayStav !== filter.status) return false;
      }
      if (filter.search) {
        const q = filter.search.toLowerCase();
        if (!String(it.name || "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [items, filter]);

  // Třídění: nejdřív NOK (nejvíc kritické), pak podle priority (High > Medium > Low),
  // pak podle posledního běhu (nejstarší nahoře, ať vidíš zaseknuté triggery).
  const sorted = useMemo(() => {
    const prioRank = { High: 0, Medium: 1, Low: 2 };
    const stavRank = { NOK: 0, OK: 1 };
    return [...filtered].sort((a, b) => {
      const sa = stavRank[a.stavPosledniRealizace] ?? 2;
      const sb = stavRank[b.stavPosledniRealizace] ?? 2;
      if (sa !== sb) return sa - sb;
      const pa = prioRank[a.priorita] ?? 9;
      const pb = prioRank[b.priorita] ?? 9;
      if (pa !== pb) return pa - pb;
      const da = a.datumPosledniRealizace ? new Date(a.datumPosledniRealizace).getTime() : 0;
      const db = b.datumPosledniRealizace ? new Date(b.datumPosledniRealizace).getTime() : 0;
      return da - db; // starší nahoře
    });
  }, [filtered]);

  // Global bypass info (jediný badge nahoře, místo per-akce labelů v tabulce)
  const globalBypassUntil = getGlobalConfirmBypassUntil("run");
  const globalBypassMinLeft = globalBypassUntil
    ? Math.max(1, Math.round((globalBypassUntil - Date.now()) / 60000))
    : 0;
  const runningCount = Object.keys(running).length;

  return (
    <Stranka>

      <ConfirmDialog
        open={!!confirmTarget}
        title="Spustit automatizaci?"
        description={
          confirmTarget
            ? `Spustit "${confirmTarget.name}" teď ručně? Běh proběhne na Netlify Scheduled Function.`
            : ""
        }
        details={confirmTarget?.id}
        confirmLabel="Spustit"
        cancelLabel="Zrušit"
        variant="primary"
        onConfirm={runNow}
        onCancel={() => setConfirmTarget(null)}
        actionKey={confirmTarget ? `run:${confirmTarget.id}` : undefined}
        bypassMinutes={60}
        bypassScope="global"
      />

      <GradientHeader
        title="Automatizace"
        napoveda="Přehled všech automatizací, které běží na Netlify. Stav, priorita, systémy a poslední běh. Automatizaci s odznakem Netlify jde spustit ručně, upravit se dá tlačítkem u záznamu a novou přidáš plusem v hlavičce. Samo se to obnovuje každých třicet sekund, když je obnovování zapnuté."
        icon={Zap}
        loading={refreshing}
        akce={[
          { klic: "pridat", ikona: Plus, popis: "Přidat automatizaci", onClick: () => setEditTarget({}) },
          {
            klic: "auto",
            ikona: autoRefresh ? Pause : Play,
            popis: autoRefresh ? "Obnovování každých 30 s je zapnuté, klepnutím vypneš" : "Obnovování je vypnuté, klepnutím zapneš",
            onClick: () => setAutoRefresh((v) => !v),
          },
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: refreshing },
        ]}
      />

      {/* Globální bypass status (1 badge místo per-akce v tabulce) */}
      {globalBypassMinLeft > 0 && (
        <div className="flex items-center justify-between gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 text-sm text-blue-900">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-blue-600" />
            <span>
              <strong>Bez potvrzení {globalBypassMinLeft} min</strong> pro všechny automatizace.
              Klik na "Spustit teď" je teď okamžitý.
            </span>
          </div>
          <button
            onClick={cancelGlobalBypass}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-white border border-blue-200 hover:bg-blue-50 text-blue-700 min-h-[44px] sm:min-h-0"
          >
            <X className="w-3 h-3" /> Zrušit
          </button>
        </div>
      )}

      {/* Doporučení max 3 paralelně */}
      {runningCount >= 3 && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span>
            Aktuálně běží <strong>{runningCount} automatizací</strong> paralelně. Doporučujeme max 3
            současně.
          </span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800">
          Chyba načítání: {error}
        </div>
      )}

      {loading && !data && (
        <>
          <SkeletonCards count={4} />
          <Card>
            <div className="text-xs text-slate-400 mb-2">Načítám stav automatizací z Notion…</div>
            <SkeletonTable rows={10} cols={9} />
          </Card>
        </>
      )}

      {data && (
        <>
          <SummaryRow items={items} filter={filter} onFilterChange={setFilter} />

          <Card>
            <Filters value={filter} onChange={setFilter} items={items} />

            <div className="text-xs text-slate-500 mb-2">
              Zobrazeno {sorted.length} z {items.length} automatizací.
              {data.cached && data.cachedAt && (
                <> Cache z {formatDateTime(data.cachedAt)}.</>
              )}
            </div>

            {/* Cards grid layout — 1 column mobile, 2 columns large */}
            {sorted.length === 0 ? (
              <div className="py-12 text-center text-slate-400">
                Žádné automatizace neodpovídají filtru.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {sorted.map((it) => {
                  const isNetlify = !!it.runnerId;
                  const displayName = it.name;
                  const lastErr = lastRun[it.name] && !lastRun[it.name].ok ? lastRun[it.name].error : null;
                  const isExpanded = expandedName === it.name;
                  const briefMd = it.popisKratky || "";
                  const detail = detailCache[it.name];
                  return (
                    <div
                      key={it.id}
                      className={`group bg-white rounded-xl border p-5 transition-all ${isExpanded ? "border-plum-300 shadow-lg" : "border-slate-200 hover:border-plum-300 hover:shadow-lg hover:-translate-y-0.5"}`}
                    >
                      {/* Card header — klikací oblast pro expand */}
                      <div
                        onClick={(e) => {
                          if (e.target.closest("button") || e.target.closest("a")) return;
                          toggleExpand(it.name);
                        }}
                        className="cursor-pointer"
                      >
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1.5">
                              <h3 className="font-semibold text-slate-900 text-base truncate group-hover:text-plum-700 transition-colors">{displayName}</h3>
                              <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5 text-xs">
                              <StavPill stav={it.status} />
                              {isNetlify ? <Pill tone="green">Netlify</Pill> : <Pill tone="slate">Apps Script</Pill>}
                              <PrioritaPill priorita={it.priorita} />
                            </div>
                          </div>
                        </div>

                        {/* Kratky popis s markdown bold */}
                        {briefMd && (
                          <div className="text-sm text-slate-700 mb-3 leading-relaxed prose-sm">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                p: ({node, ...props}) => <p className="m-0" {...props} />,
                                strong: ({node, ...props}) => <strong className="text-slate-900 font-bold" {...props} />,
                              }}
                            >
                              {briefMd}
                            </ReactMarkdown>
                          </div>
                        )}
                      </div>

                      {/* Meta row — Plán + Posledně + Stav */}
                      <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                        <div className="bg-slate-50 rounded-xl px-2.5 py-2">
                          <div className="text-slate-400 text-[11px] uppercase tracking-wide mb-0.5">Plán</div>
                          <div className="font-medium text-slate-700 leading-tight">{it.cronScheduleLabel || it.triggerLabel || "—"}</div>
                        </div>
                        <div className="bg-slate-50 rounded-xl px-2.5 py-2">
                          <div className="text-slate-400 text-[11px] uppercase tracking-wide mb-0.5">Posledně</div>
                          <div className="font-medium text-slate-700 leading-tight">{relativeTime(it.datumPosledniRealizace) || "—"}</div>
                        </div>
                        <div className="bg-slate-50 rounded-xl px-2.5 py-2">
                          <div className="text-slate-400 text-[11px] uppercase tracking-wide mb-0.5">Stav</div>
                          <div className="font-medium leading-tight"><StatusPill status={it.stavPosledniRealizace} /></div>
                        </div>
                      </div>

                      {/* Error pod meta row */}
                      {lastErr && (
                        <div className="mb-3 px-3 py-2 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700 truncate" title={lastErr}>
                          ✗ {lastErr}
                        </div>
                      )}

                      {/* Systémy badges */}
                      <div className="flex flex-wrap gap-1 mb-3">
                        {(it.systemy || []).map((s) => (
                          <Pill key={s} tone="blue">{s}</Pill>
                        ))}
                      </div>

                      {/* Akce — Spustit teď + Notion */}
                      <div className="flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
                        <div>
                          {isNetlify ? (
                            (() => {
                              const now = Date.now();
                              const isRunning = !!running[it.name];
                              const elapsedSec = isRunning ? Math.floor((now - running[it.name].startedAt) / 1000) : 0;
                              const lr = lastRun[it.name];
                              const sinceFinishMs = lr ? (now - lr.at) : null;
                              const isDone = lr && lr.ok && sinceFinishMs !== null && sinceFinishMs < 5000;
                              const isCooldown = lr && lr.ok && sinceFinishMs !== null && sinceFinishMs >= 5000 && sinceFinishMs < 25000;
                              const cooldownSec = isCooldown ? Math.ceil((25000 - sinceFinishMs) / 1000) : 0;
                              const isError = lr && !lr.ok && sinceFinishMs !== null && sinceFinishMs < 10000;
                              let className, text, icon, disabled = false, title;
                              if (isRunning) {
                                className = "bg-blue-500 hover:bg-blue-600 text-white cursor-default";
                                icon = <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />;
                                text = `Běží ${elapsedSec}s`;
                                disabled = true;
                                title = `Běží ${elapsedSec}s`;
                              } else if (isDone) {
                                className = "bg-emerald-500 text-white cursor-default";
                                icon = <CheckCircle2 className="w-3.5 h-3.5" />;
                                text = lr.background ? "Spuštěno" : "Hotovo";
                                disabled = true;
                              } else if (isCooldown) {
                                className = "bg-slate-200 text-slate-500 cursor-not-allowed";
                                icon = <Clock className="w-3.5 h-3.5" />;
                                text = `Počkej ${cooldownSec}s`;
                                disabled = true;
                              } else if (isError) {
                                className = "bg-rose-500 hover:bg-rose-600 text-white";
                                icon = <AlertTriangle className="w-3.5 h-3.5" />;
                                text = "Zkusit znovu";
                              } else if (isThrottleActive) {
                                const throttleSec = Math.ceil((globalThrottleUntil - now) / 1000);
                                className = "bg-amber-100 text-amber-800 cursor-not-allowed border border-amber-300";
                                icon = <AlertTriangle className="w-3.5 h-3.5" />;
                                text = `Počkej ${throttleSec}s`;
                                disabled = true;
                              } else {
                                className = "bg-amber-500 hover:bg-amber-600 text-white";
                                icon = <Play className="w-3.5 h-3.5" />;
                                text = "Spustit teď";
                              }
                              return (
                                <button
                                  onClick={disabled ? undefined : () => askRunConfirm(it.name, it.runnerId)}
                                  disabled={disabled}
                                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-medium shadow-sm transition ${className}`}
                                  title={title}
                                >
                                  {icon}
                                  {text}
                                </button>
                              );
                            })()
                          ) : (
                            <span className="text-xs text-slate-400 italic">Apps Script (read-only)</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setEditTarget(it)}
                            className="text-xs text-slate-500 hover:text-plum-600 transition-colors"
                            title="Upravit záznam v Postgres (popis, plán, runner, kategorie...)"
                          >
                            Upravit
                          </button>
                          <button
                            onClick={() => setDeleteTarget(it)}
                            className="text-xs text-slate-400 hover:text-rose-600 transition-colors"
                            title="Smazat automatizaci z Postgres (cron sám zůstane, jen se přestane zobrazovat v UI)"
                          >
                            Smazat
                          </button>
                          {it.url && (
                            <a
                              href={it.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
                              title="Otevřít backup v Notion (read-only mirror)"
                            >
                              Notion <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Drop-down detail (expandable) */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-slate-200 -mx-5 -mb-5 px-5 pb-5 bg-gradient-to-br from-slate-50 to-white rounded-b-xl">
                          {!detail ? (
                            <div className="py-8 text-center text-slate-500 text-sm">Načítám detail…</div>
                          ) : detail.error ? (
                            <div className="text-rose-600 text-sm">{detail.error}</div>
                          ) : (
                            <>
                              {detail.popis_md ? (
                                <article className="prose prose-sm prose-slate max-w-none prose-headings:mt-4 prose-headings:mb-2 prose-headings:font-bold prose-headings:text-slate-900 prose-h1:text-lg prose-h2:text-base prose-h2:border-b prose-h2:border-slate-200 prose-h2:pb-1 prose-h3:text-sm prose-p:my-2 prose-p:leading-relaxed prose-li:my-0.5 prose-strong:text-slate-900 prose-strong:font-semibold prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:before:content-none prose-code:after:content-none prose-table:text-xs prose-th:bg-slate-100 prose-th:font-semibold prose-th:p-2 prose-td:p-2 prose-td:border-t prose-td:border-slate-200 prose-hr:my-4">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{detail.popis_md}</ReactMarkdown>
                                </article>
                              ) : (
                                <div className="text-sm text-slate-500 italic">Bez detailního popisu. Klikni Upravit a doplň pole „Popis (markdown)" v modalu.</div>
                              )}
                              {detail.notion_url && (
                                <div className="mt-5 pt-4 border-t border-slate-200">
                                  <a href={detail.notion_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-plum-600 hover:text-plum-700 font-medium">
                                    Otevřít plný popis v Notion <ExternalLink className="w-3.5 h-3.5" />
                                  </a>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

          </Card>
        </>
      )}

      {/* Edit / Pridat modal — CRUD pro PG `automatizace` tabulku. */}
      {editTarget && (
        <EditAutomatizaceModal
          target={editTarget}
          runners={runners}
          onClose={() => setEditTarget(null)}
          onSaved={async () => { setEditTarget(null); await refetch(); }}
        />
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <DeleteAutomatizaceModal
          target={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={async () => { setDeleteTarget(null); await refetch(); }}
        />
      )}
    </Stranka>
  );
}

// ─── EditAutomatizaceModal ─────────────────────────────────────────────────
// Pro pridani / upravu zaznamu v PG automatizace tabulce. Pri prazdnem target
// (`{}`) = pridat novou. Pri target s name = upravit existujici.
function EditAutomatizaceModal({ target, runners, onClose, onSaved }) {
  const isNew = !target.name;
  const [name, setName] = React.useState(target.name || "");
  const [icon, setIcon] = React.useState(target.icon || "");
  const [kategorie, setKategorie] = React.useState(target.kategorie || "");
  const [priorita, setPriorita] = React.useState(target.priorita || "Medium");
  const [status, setStatus] = React.useState(target.status || "Aktivní");
  const [systemy, setSystemy] = React.useState((target.systemy || []).join(", "));
  const [popisKratky, setPopisKratky] = React.useState(target.popisKratky || "");
  const [runnerId, setRunnerId] = React.useState(target.runnerId || "");
  const [cronScheduleLabel, setCronScheduleLabel] = React.useState(target.cronScheduleLabel || "");
  const [cronSchedule, setCronSchedule] = React.useState(target.cronSchedule || "");
  const [frequencyLabel, setFrequencyLabel] = React.useState(target.frequencyLabel || "denně");
  const [frequencyIntervalH, setFrequencyIntervalH] = React.useState(target.frequencyIntervalH || 24);
  const [background, setBackground] = React.useState(target.background === true);
  const [aktivni, setAktivni] = React.useState(target.aktivni !== false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const save = async () => {
    if (!name.trim()) { setError("Název je povinný"); return; }
    setError(""); setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        icon: icon.trim() || null,
        kategorie: kategorie.trim() || null,
        priorita: priorita || null,
        status: status || "Aktivní",
        systemy: systemy.split(",").map((s) => s.trim()).filter(Boolean),
        popisKratky: popisKratky.trim() || null,
        runnerId: runnerId || null,
        cronSchedule: cronSchedule.trim() || null,
        cronScheduleLabel: cronScheduleLabel.trim() || null,
        frequencyLabel: frequencyLabel || null,
        frequencyIntervalH: parseInt(frequencyIntervalH, 10) || null,
        background,
        aktivni,
      };
      const r = await apiFetch("/api/automatizace-status", {
        method: "POST",
        body: JSON.stringify({ action: "upsert", payload }),
      });
      const j = await r.json();
      if (!j.ok) { setError(j.error || "Neznámá chyba"); setSaving(false); return; }
      await onSaved();
    } catch (e) {
      setError(e.message); setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-dark/50 backdrop-blur-sm p-4 pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">
            {isNew ? "Přidat automatizaci" : `Upravit: ${target.name}`}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-800">{error}</div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Název *</label>
              <input value={name} onChange={(e) => setName(e.target.value)} disabled={!isNew} className="field w-full disabled:bg-slate-50" />
              {!isNew && <div className="text-[11px] text-slate-400 mt-0.5">Název je primary key. Pro přejmenování smaž a vytvoř znovu.</div>}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Ikona (emoji)</label>
              <input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="🔄" className="field w-full" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Kategorie</label>
              <input value={kategorie} onChange={(e) => setKategorie(e.target.value)} placeholder="Sync, FAPI, Operations..." className="field w-full" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Priorita</label>
              <select value={priorita} onChange={(e) => setPriorita(e.target.value)} className="field w-full">
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="field w-full">
                <option value="Aktivní">Aktivní</option>
                <option value="V přípravě">V přípravě</option>
                <option value="Backlog">Backlog</option>
                <option value="Archived">Archived</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Systémy (oddělené čárkou)</label>
              <input value={systemy} onChange={(e) => setSystemy(e.target.value)} placeholder="Databáze, FAPI, Slack" className="field w-full" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Krátký popis (markdown, 1-2 věty)</label>
              <textarea value={popisKratky} onChange={(e) => setPopisKratky(e.target.value)} rows={3} className="field w-full resize-y" />
            </div>
            <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
              <div className="text-xs font-semibold text-slate-700 mb-2">Spouštění</div>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Runner ID (pro Spustit teď)</label>
              <select value={runnerId} onChange={(e) => setRunnerId(e.target.value)} className="field w-full">
                <option value="">Bez runneru (read-only)</option>
                {runners.map((r) => (
                  <option key={r.id} value={r.id}>{r.id}: {r.name}</option>
                ))}
              </select>
              <div className="text-[11px] text-slate-400 mt-0.5">Musí být klíč v REGISTRY automatizace-run.js. Změnu kódu řeš v repu.</div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Cron (UTC)</label>
              <input value={cronSchedule} onChange={(e) => setCronSchedule(e.target.value)} placeholder="0 5 * * *" className="field w-full font-mono" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Lidský popis plánu</label>
              <input value={cronScheduleLabel} onChange={(e) => setCronScheduleLabel(e.target.value)} placeholder="denně 7:00 CEST" className="field w-full" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Frekvence</label>
              <select value={frequencyLabel} onChange={(e) => { setFrequencyLabel(e.target.value); const map = { "hodinově": 1, "denně": 24, "týdně": 24*7, "měsíčně": 24*30 }; if (map[e.target.value]) setFrequencyIntervalH(map[e.target.value]); }} className="field w-full">
                <option value="hodinově">hodinově</option>
                <option value="denně">denně</option>
                <option value="týdně">týdně</option>
                <option value="měsíčně">měsíčně</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Interval (hodin)</label>
              <input type="number" value={frequencyIntervalH} onChange={(e) => setFrequencyIntervalH(e.target.value)} className="field w-full" />
            </div>
            <div className="col-span-2 flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={background} onChange={(e) => setBackground(e.target.checked)} className="rounded border-slate-300" />
                <span>Background function (~5-15 min běh)</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={aktivni} onChange={(e) => setAktivni(e.target.checked)} className="rounded border-slate-300" />
                <span>Aktivní (zobrazit v default filtru)</span>
              </label>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50 rounded-b-xl">
          <button onClick={onClose} disabled={saving} className="btn btn-secondary">Zrušit</button>
          <button onClick={save} disabled={saving || !name.trim()} className="btn btn-primary">
            {saving ? "Ukládám…" : (isNew ? "Přidat" : "Uložit")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── DeleteAutomatizaceModal ────────────────────────────────────────────────
function DeleteAutomatizaceModal({ target, onClose, onDeleted }) {
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState("");
  const remove = async () => {
    setError(""); setDeleting(true);
    try {
      const r = await apiFetch("/api/automatizace-status", {
        method: "POST",
        body: JSON.stringify({ action: "delete", name: target.name }),
      });
      const j = await r.json();
      if (!j.ok) { setError(j.error || "Neznámá chyba"); setDeleting(false); return; }
      await onDeleted();
    } catch (e) { setError(e.message); setDeleting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-dark/50 backdrop-blur-sm p-4 pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Smazat automatizaci?</h3>
          <p className="text-sm text-slate-600 mb-1">Smaže záznam z PG tabulky <code className="text-xs bg-slate-100 px-1 rounded">automatizace</code>.</p>
          <p className="text-sm text-slate-600 mb-3"><strong>{target.name}</strong> už nebude vidět v UI.</p>
          <p className="text-xs text-slate-500 mb-1">Pozor: cron v <code className="text-xs">netlify/functions/scheduled-*.mjs</code> tím <strong>nezrušíš</strong>, ten zůstane běžet dál (jen ho neuvidíš). Pro úplné vypnutí cronu smaž config schedule v .mjs souboru.</p>
          {error && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-800 mt-3">{error}</div>}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50 rounded-b-xl">
          <button onClick={onClose} disabled={deleting} className="btn btn-secondary">Zrušit</button>
          <button onClick={remove} disabled={deleting} className="btn btn-danger">
            {deleting ? "Mažu…" : "Smazat"}
          </button>
        </div>
      </div>
    </div>
  );
}
